import { Link } from 'react-router-dom';

import { panelClasses } from './panel-classes';

import type { CSSProperties, ElementType, ReactElement, ReactNode } from 'react';

/**
 * Bedrock surface primitives — the card, the linked card, the pill, the
 * dot-item and the stat grid. These cover essentially every "box" the marketing
 * site draws; anything more elaborate is a demo, and demos own their own chrome.
 */

/* ------------------------------------------------------------------ */
/* Panel                                                               */
/* ------------------------------------------------------------------ */

export interface PanelProps {
  /** Paint for a dark band ground rather than the light page. */
  readonly onDark?: boolean;
  /** A 3px brand rule across the top edge — marks a "result" card. */
  readonly accent?: boolean;
  /** Lift on hover. Only for panels that are themselves a link. */
  readonly interactive?: boolean;
  readonly as?: ElementType;
  readonly className?: string;
  readonly style?: CSSProperties;
  readonly children: ReactNode;
  /**
   * Accessible name, for a Panel rendered `as` a landmark element.
   *
   * These are forwarded EXPLICITLY, because a component does not receive
   * `aria-*` the way an intrinsic element does — and TypeScript does not say
   * so. `<Panel as="section" aria-label="…">` typechecks clean and drops the
   * attribute on the floor, producing an unnamed landmark: invisible in
   * review, invisible to axe (an unnamed `<section>` is not a landmark at all,
   * so there is nothing for it to complain about), and wrong only to the
   * screen-reader user trying to choose between the page's regions.
   */
  readonly 'aria-label'?: string;
  readonly 'aria-labelledby'?: string;
}

/** The standard card. Styling lives in `panelClasses`. */
export function Panel({
  onDark = false,
  accent = false,
  interactive = false,
  as: Tag = 'div',
  className,
  style,
  children,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}: PanelProps): ReactElement {
  return (
    <Tag
      className={panelClasses({ onDark, interactive, className })}
      style={style}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
    >
      {accent ? (
        <span
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-[3px]"
          style={{ background: 'var(--bd-gradient)' }}
        />
      ) : null}
      {children}
    </Tag>
  );
}

/* ------------------------------------------------------------------ */
/* LinkPanel                                                           */
/* ------------------------------------------------------------------ */

export interface LinkPanelProps extends Omit<PanelProps, 'as' | 'interactive'> {
  /** Resolved href. Route-relative for internal, absolute for external. */
  readonly href: string;
  /** Render an `<a target="_blank">` instead of a router `<Link>`. */
  readonly external?: boolean;
}

/**
 * A card that is itself the link — the whole card is the target, per
 * `.claude/rules/ui-design.md` § 17 ("rows that open something"). It takes the
 * simple shape of that rule: nothing else inside is interactive, so the real
 * control wraps the content and there is no delegation, no guards, and no
 * `role`/`tabIndex` on a `<div>` to strand a keyboard user with.
 *
 * `h-full` so a card in a grid row fills the tallest sibling; without it a
 * two-line card leaves a hole in its own hover target.
 */
export function LinkPanel({
  href,
  external = false,
  onDark = false,
  accent = false,
  className,
  style,
  children,
}: LinkPanelProps): ReactElement {
  const classes = panelClasses({
    onDark,
    interactive: true,
    className: ['block h-full', className].filter(Boolean).join(' '),
  });
  const body = (
    <>
      {accent ? (
        <span
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-[3px]"
          style={{ background: 'var(--bd-gradient)' }}
        />
      ) : null}
      {children}
    </>
  );
  return external ? (
    <a href={href} target="_blank" rel="noopener noreferrer" className={classes} style={style}>
      {body}
    </a>
  ) : (
    <Link to={href} className={classes} style={style}>
      {body}
    </Link>
  );
}

/* ------------------------------------------------------------------ */
/* Pill                                                                */
/* ------------------------------------------------------------------ */

export interface PillProps {
  readonly onDark?: boolean;
  /** A leading dot in this colour — used to key a pill to a chart series. */
  readonly dotColor?: string;
  readonly className?: string;
  readonly children: ReactNode;
}

/** The small bordered chip: capability tags, "Claude / ChatGPT / Gemini". */
export function Pill({ onDark = false, dotColor, className, children }: PillProps): ReactElement {
  return (
    <span
      className={[
        'inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs',
        onDark
          ? 'border border-[color:var(--bd-band-line)] bg-[color:var(--bd-band-fill)] text-[color:var(--bd-band-body)]'
          : 'border border-[color:var(--bd-line-2)] bg-[color:var(--bd-page)] text-[color:var(--bd-body)]',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {dotColor ? (
        <span
          aria-hidden="true"
          className="size-1.5 flex-none rounded-full"
          style={{ background: dotColor }}
        />
      ) : null}
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* DotItem                                                             */
/* ------------------------------------------------------------------ */

export interface DotItemProps {
  readonly color: string;
  readonly title: ReactNode;
  readonly onDark?: boolean;
  readonly children: ReactNode;
}

/**
 * A coloured-bullet claim: a dot, a bold line, a sentence. The control band's
 * four guarantees. Renders as an `<li>` — callers wrap in a `<ul>`, so the
 * list is announced as a list rather than as four loose paragraphs.
 */
export function DotItem({ color, title, onDark = false, children }: DotItemProps): ReactElement {
  return (
    <li className="flex items-start gap-3.5">
      <span
        aria-hidden="true"
        className="mt-[7px] size-2 flex-none rounded-full"
        style={{ background: color }}
      />
      <span className="min-w-0">
        <span
          className={[
            'block text-[0.95rem] font-semibold',
            onDark ? 'text-[color:var(--bd-band-ink)]' : 'text-[color:var(--bd-ink)]',
          ].join(' ')}
        >
          {title}
        </span>
        <span
          className={[
            'mt-1.5 block text-pretty text-[0.85rem] leading-[1.6]',
            onDark ? 'text-[color:var(--bd-band-dim)]' : 'text-[color:var(--bd-muted)]',
          ].join(' ')}
        >
          {children}
        </span>
      </span>
    </li>
  );
}

/* ------------------------------------------------------------------ */
/* StatGrid                                                            */
/* ------------------------------------------------------------------ */

export interface Stat {
  readonly value: string;
  readonly label: string;
  /** Paint the value with the brand gradient — at most ONE per grid. */
  readonly accent?: boolean;
}

export interface StatGridProps {
  readonly stats: readonly Stat[];
  readonly className?: string;
}

/**
 * The hairline-separated stat row ("214 screens mapped · 38 actions · 26
 * rules"). The separators are a 1px background showing through a grid gap,
 * which is why there is no per-cell border to get wrong at the edges.
 */
export function StatGrid({ stats, className }: StatGridProps): ReactElement {
  return (
    <dl
      className={[
        'grid gap-px overflow-hidden rounded-xl border border-[color:var(--bd-line)]',
        'bg-[color:var(--bd-line)]',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ gridTemplateColumns: `repeat(${stats.length}, minmax(0,1fr))` }}
    >
      {stats.map((stat) => (
        <div key={stat.label} className="bg-[color:var(--bd-page)] px-3.5 py-3">
          <dd
            className={[
              'text-2xl font-semibold leading-none tracking-[-0.04em]',
              stat.accent ? 'bd-grad-text' : 'text-[color:var(--bd-ink)]',
            ].join(' ')}
          >
            {stat.value}
          </dd>
          <dt className="mt-1.5 text-[11.5px] leading-tight text-[color:var(--bd-subtle)]">
            {stat.label}
          </dt>
        </div>
      ))}
    </dl>
  );
}
